

import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {Prop} from "vue-property-decorator";
import {IAuthUserData} from "@/store/modules/authModule";
import {UrlHelper} from "@/helpers/UrlHelper";
import {RawLocation} from "vue-router";
import {EPermissionActions} from "@/model/EPermissionActions";
import {setLocale} from "@/helpers/translations";
import {ELanguages} from "@/locale";
import {ILocker} from "@/model/Locker/lockerInterfaces";
import {PermissionHelper} from "@/helpers/PermissionHelper";
import {EUserPermissions} from "@/model/EUserPermissions";

const packageJson = require('../../package.json');

  const authModule = namespace("authModule");
  const lockerModule = namespace("lockerModule");
  const standaloneModule = namespace("standaloneModule");
  const communityModule = namespace("communityModule");

  // Swagger and SSH connections had no own action: they were visible with any of the DEV section actions
  const DEV_TOOLS_ACTIONS = [
    EPermissionActions.ACTION_SWAGGER,
    EPermissionActions.ACTION_SWAGGER_G1,
    EPermissionActions.ACTION_API_KEYS_AUTH_MANAGEMENT,
    EPermissionActions.ACTION_SETTINGS_AUDIT_LOGS,
  ];

  interface INavItem {
    title: string;
    icon: string;
    to?: RawLocation|any;
    urlPattern?: RegExp[];
    items?: INavItem[];
    actions?: string[];
    onlyIf?: any;
  }

  @Component({
    name: 'LeftNav',
  })
  export default class LeftNv extends Vue {

    @Prop()
    protected miniVariant!: boolean;
    @Prop()
    protected color!: string;
    @authModule.Getter('userData')
    protected userData!: IAuthUserData;
    @lockerModule.Getter('locker')
    protected locker!: ILocker;

    @standaloneModule.Getter('hasStandalones')
    protected hasStandalones!: boolean;

    @communityModule.Getter('pinSectionVisible')
    protected pinSectionVisible!: boolean;

    @communityModule.Action('checkPinSectionVisibility')
    private checkPinSectionVisibility!: () => Promise<any>;

    get navItems(): INavItem[] {
      // I only include these constants so they trigger a refresh
      const userData: any = this.userData;
      const locker: any = this.locker;
      // @polpujado : pinSectionVisible is read here so Vue tracks it as a reactive dep;
      // its value is used directly in the filter below
      const canSeePins = this.userIsDeveloper() || this.pinSectionVisible;
      const items = canSeePins
        ? this.allNavItems
        : this.allNavItems.filter(item => item.title !== 'menu.communities.securityCodes');
      // @ts-ignore
      return this.transformNavItems(items, userData, locker, this.hasStandalones);
    }

    get version() {
      return packageJson.version;
    }

    created() {
      this.$uiBus.on("closeDrawer", this.onCloseDrawer);
      this.$store.dispatch('standaloneModule/checkStandalonesExist');
      this.checkPinSectionVisibility();
    }

    protected drawer: boolean = true;
    protected allNavItems: INavItem[] = [
      { title: 'menu.clients.title', icon: 'mdi-account-group',
        to: {name: 'businessClientList'},
        actions: [EPermissionActions.ACTION_BUSINESS_CLIENTS],
        urlPattern: [/^\/clients\//],
      },
      { title: 'menu.communities.title', icon: 'mdi-domain',
        to: {name: 'communityList'},
        actions: [EPermissionActions.ACTION_COMMUNITIES],
        urlPattern: [/^\/communities\//],
      },
      {
        title: 'menu.communities.securityCodes',
        icon: 'mdi-lock',
        to: {name: 'securityCodeList'},
        urlPattern: [/security-codes\//],
      },
      { title: 'menu.lockers.title', icon: this.$icons.lockers.icon,
        actions: [
          EPermissionActions.ACTION_LOCKERS,
          EPermissionActions.ACTION_LOCKERS_INSTALL,
          EPermissionActions.ACTION_LOCKERS_OPENCELL,
        ],
        urlPattern: [/lockers\/\d+/, /lockers\/list\/?$/, /install\/\d+/, /install\/list\/?$/, /features\/?$/, ],
        items: [
          { title: 'menu.lockers.list', icon: this.$icons.list, to: {name: 'lockerList'},},
          { title: 'menu.lockers.install', icon: this.$icons.lockers.install.icon,
            to: {name: 'installList'},
            actions: [
              EPermissionActions.ACTION_LOCKERS_INSTALL,
              EPermissionActions.ACTION_LOCKERS_OPENCELL,
              EPermissionActions.ACTION_LOCKERS,
            ]
          },
          { title: 'common.features', icon: this.$icons.lockers.features,
            to: {name: 'lockerFeatures'},
            actions: [
              EPermissionActions.ACTION_LOCKERS_INSTALL,
              EPermissionActions.ACTION_LOCKERS
            ],
          },
        ]},
      { title: 'menu.standalones.title', icon: 'mdi-cellphone-link',
        to: {name: 'allStandalonesList'},
        actions: [EPermissionActions.ACTION_LOCKERS, EPermissionActions.ACTION_LOCKERS_INSTALL],
        onlyIf: () => this.hasStandalones || this.userIsDeveloperOrAdmin(),
      },
      { title: 'menu.users.title', icon: this.$icons.users.icon,
        to: {name: 'userList'},
        actions: [EPermissionActions.ACTION_USERS,],
        urlPattern: [/^\/users\/(list|\d+(?!\d|\/webhooks))/],
      },
      // Subscriptions live as a tab inside the orders section (OrderSectionTabs)
      { title: 'menu.orders.title', icon: this.$icons.orders.orders,
        to: {name: 'orderList'},
        actions: [EPermissionActions.ACTION_ORDERS],
        urlPattern: [/^\/orders\//, /^\/subscriptions\//],
      },
      // No group-level actions: the section is visible when any of its items is. Each item keeps
      // exactly the actions and urlPattern it had as a top-level entry.
      { title: 'menu.operations.title', icon: 'mdi-cog-transfer',
        urlPattern: [/^\/invoices\//, /helpdesk/, /^\/routes\//],
        items: [
          { title: 'menu.invoices.title', icon: this.$icons.invoices.icon,
            to: {name: 'invoiceList'},
            actions: [EPermissionActions.ACTION_INVOICES],
            urlPattern: [/^\/invoices\//],
          },
          // Helpdesk pages are reached through HelpdeskNav (top nav); urlPattern keeps it highlighted on all of them
          { title: 'menu.helpdesk.title',
            icon: 'mdi-lifebuoy',
            to: {name: 'helpdeskDashboard'},
            urlPattern: [/helpdesk/],
            actions: [EPermissionActions.ACTION_HELPDESK_ACCESS, EPermissionActions.ACTION_HELPDESK_ADMIN],
          },
          { title: 'menu.routes.title', icon: this.$icons.routes.icon,
            to: {name: 'routeList'},
            actions: [EPermissionActions.ACTION_ROUTES_VIEW, EPermissionActions.ACTION_ROUTES_CREATE, EPermissionActions.ACTION_ROUTES_EDIT, EPermissionActions.ACTION_ROUTES_DELETE, EPermissionActions.ACTION_ROUTES_ADMIN, EPermissionActions.ACTION_ROUTES_SEE_MY_ROUTES],
            urlPattern: [/^\/routes\//],
          },
        ],
      },
      { title: 'menu.providers.title', icon: this.$icons.serviceProviders.icon,
        onlyIf: () => this.$canAction(EPermissionActions.ACTION_SERVICE_PROVIDERS),
        urlPattern: [
          /serviceProviders\/\d+/,
          /serviceProviders\/list\/?$/,
          /serviceProviders\/categories\/?/,
          /serviceProviders\/timeline\/?$/,
          /service-providers\/metrics\/?$/,
          /^\/products\/?$/,
        ],
        items: [
          { title: 'menu.providers.list', icon: this.$icons.list,
            to: {name: 'serviceProviderList'},
            actions: [
              EPermissionActions.ACTION_SERVICE_PROVIDERS,
            ],
          },
          { title: 'menu.providers.categories', icon: this.$icons.categories,
            to: {name: 'serviceProviderCategories'},
            actions: [
              EPermissionActions.ACTION_SERVICE_PROVIDERS,
            ],
          },
          { title: 'menu.providers.timeline',
            icon: this.$icons.timeLineNotes,
            to: {name: 'serviceProviderTimeLine'},
            actions: [
              EPermissionActions.ACTION_REPORTS,
            ],
          },
          { title: 'menu.providers.metrics', icon: 'mdi-chart-bar',
            to: { name: 'serviceProviderMetrics' },
            actions: [EPermissionActions.ACTION_SERVICE_PROVIDERS_METRICS, EPermissionActions.ACTION_SERVICE_PROVIDERS_METRICS_READ],
          },
          { title: 'menu.providers.products', icon: 'mdi-package-variant',
            to: {name: 'products'},
            actions: [EPermissionActions.ACTION_SERVICE_PROVIDERS],
          },
        ]
      },
      { title: 'menu.reports.title',
        icon: this.$icons.reports.icon,
        actions: [EPermissionActions.ACTION_REPORTS],
        urlPattern: [/^\/reports/],
        items: [
          { title: 'menu.reports.general', icon: 'mdi-download', to: {name: 'reportsGeneral'}, actions: [EPermissionActions.ACTION_REPORTS] },
          { title: 'menu.reports.datastudio', icon: 'mdi-google-analytics', to: {name: 'reports'}, actions: [EPermissionActions.ACTION_REPORTS] },
          { title: 'menu.reports.registrations', icon: 'mdi-chart-bar-stacked', to: {name: 'reportsRegistrations'}, actions: [EPermissionActions.ACTION_REPORTS] },
        ],
      },
      { title: 'menu.permissions.title', icon: this.$icons.permissions.icon,
        actions: [
          EPermissionActions.ACTION_SETTINGS_PERMISSIONS,
        ],
        urlPattern: [/permissions\/\d+/,],
        items: [
          { title: 'menu.permissions.whoHas', icon: this.$icons.permissions.whoHas,
            to: { name: 'permissionWhoHas'},
          },
          { title: 'menu.permissions.permissionMap', icon: this.$icons.permissions.permissionMap,
            to: { name: 'permissionMap'},
          },
        ]},
      // No group-level actions: the section is visible when any of its items is
      { title: 'menu.settings.title', icon: this.$icons.settings.icon,
        actions: [
            EPermissionActions.ACTION_LANDINGPAGES,
            EPermissionActions.ACTION_SETTINGS,
            EPermissionActions.ACTION_LOCKER_COMPANIES,
            EPermissionActions.ACTION_CHANNELS,
            EPermissionActions.ACTION_IDENTITY_INTEGRATIONS,
            EPermissionActions.ACTION_SETTINGS_COURIERS,
        ],
        urlPattern: [/settings\//,],
        items: [
          { title: 'menu.settings.landings', icon: this.$icons.settings.landings, to: {name: 'landingpages'}, actions: [EPermissionActions.ACTION_LANDINGPAGES,]},
          { title: 'menu.settings.messages', icon: this.$icons.settings.emails, to: {name: 'messages'}, actions: [EPermissionActions.ACTION_SETTINGS] },
          { title: 'menu.settings.couriers', icon: this.$icons.settings.couriers, to: {name: 'couriers'}, actions: [EPermissionActions.ACTION_SETTINGS_COURIERS,]},
          { title: 'menu.companies.title', icon: this.$icons.companies.icon, to: {name: 'lockerCompaniesList'}, actions: [EPermissionActions.ACTION_LOCKER_COMPANIES,]},
          { title: 'menu.rentback.title', icon: 'mdi-cash-refund', to: {name: 'rentbackList'}, actions: [EPermissionActions.ACTION_COMMUNITIES]},
          { title: 'menu.lockers.screen_savers', icon: this.$icons.screensavers.screensaver, to: {name: 'screensavers'}, actions: [EPermissionActions.ACTION_SCREENSAVERS]},
        ]},
      { title: 'menu.panel.title', icon: this.$icons.panel.icon, to: '/mayor', actions: [EPermissionActions.ACTION_REPORTS]},
      // No group-level actions: the section is visible when any of its items is
      { title: 'DEV',
        icon: this.$icons.developer,
        urlPattern: [
          /^\/swagger\//,
          /^\/sshconnections/,
          /^\/settings\/(apiKeys|audit-logs|channels|modules|identity-integrations|translations)/,
          /^\/users\/(\d+\/)?webhooks/,
          /^\/users\/diffusionExpress/,
          /^\/users\/import/,
        ],
        items: [
          { title: 'menu.swagger.module', 'icon': this.$icons.swagger.icon, to: '/swagger/modulo', actions: DEV_TOOLS_ACTIONS },
          { title: 'menu.sshconnection.module', 'icon': 'mdi-ssh', to: '/sshconnections', actions: DEV_TOOLS_ACTIONS },
          { title: 'menu.apiKeys.module', 'icon': 'mdi-key-variant', to: '/settings/apiKeys',
            actions: [EPermissionActions.ACTION_API_KEYS_AUTH_MANAGEMENT],
          },
          { title: 'menu.auditLogs.module', 'icon': 'mdi-history', to: {name: 'auditLogs'},
            actions: [EPermissionActions.ACTION_SETTINGS_AUDIT_LOGS],
          },
          { title: 'menu.settings.channels', icon: this.$icons.channels.channels, to: {name: 'channels'}, actions: [EPermissionActions.ACTION_CHANNELS,]},
          { title: 'menu.settings.modules', icon: this.$icons.settings.modules, to: {name: 'settingsModules'}, actions: [EPermissionActions.ACTION_COMMUNITIES_ADMIN,]},
          { title: 'menu.settings.identityIntegrations', icon: 'mdi-account-key-outline', to: {name: 'identityIntegrations'}, actions: [EPermissionActions.ACTION_IDENTITY_INTEGRATIONS,]},
          { title: 'menu.settings.translations', icon: this.$icons.settings.translations, to: {name: 'translations'}, onlyIf: () => this.userIsAdmin() },
          { title: 'menu.webhooks.title', icon: 'mdi-link', to: { name: 'userWebhooks'},
            actions: [
              EPermissionActions.ACTION_ORDERS_WEBHOOK,
              EPermissionActions.ACTION_USERS_WEBHOOK,
              EPermissionActions.ACTION_WEBHOOK_ADMIN,
            ],
          },
          { title: 'menu.userImport', icon: this.$icons.users.import,
            to: {name: 'userImport'},
            actions: [EPermissionActions.ACTION_USERS_IMPORT,],
          },
          { title: 'menu.diffusionExpress', icon: this.$icons.users.import,
            to: {name: 'diffusionExpress'},
            actions: [EPermissionActions.ACTION_USERS_DIFFUSION_EXPRESS,],
          },
        ]
      },
    ];

    protected gotoMyPermissions() {
      const userId: string = this.userData && this.userData.id ? this.userData.id.toString() : '';
      const isMyPermissionPage = this.$route.name == 'userPermissions';
      if (userId && !isMyPermissionPage) {
        this.$router.push({name: 'userPermissions', params: {userId: userId}});
      }
    }
    protected onLogout() {
      this.$store.dispatch('authModule/logout');
    }
    protected transformNavItems(allNavItems: INavItem[]) {
      if (!this.userData || !this.userData.permissions) {
        return [];
      }
      let navItems: INavItem[] = [];
      allNavItems.forEach((eachNavItem: INavItem) => {
        let hasPermission = true;
        if (eachNavItem.actions) {
          hasPermission = false;
          eachNavItem.actions.forEach(each => {
            if (this.$canAction(each)) {
              hasPermission = true;
            }
          });
        }

        if (!hasPermission) {
          return;
        }

        if (eachNavItem.onlyIf && !eachNavItem.onlyIf()) {
          return;
        }

        const items = eachNavItem.items ? this.transformNavItems(eachNavItem.items) : undefined;
        // A section without visible items is hidden
        if (items && !items.length) {
          return;
        }

        navItems.push({
          icon: eachNavItem.icon,
          title: this.$t(eachNavItem.title).toString(),
          to: this.urlGetter(eachNavItem.to),
          items,
          urlPattern: eachNavItem.urlPattern
        });

      });

      return navItems;

    }
    protected menuIsOpen(item: INavItem) {
      let ret = false;
      (item.urlPattern || []).forEach(
          eachPattern => ret = ret || !!this.$route.path.match(eachPattern)
      );
      return ret;
    }
    protected urlGetter(originalTo?: any) {

      if (!originalTo) {
        return '';
      }

      let ret = originalTo;

      if (typeof ret == "string") {

        if (originalTo.indexOf(':userId') !== -1) {
          ret = this.$route.params['userId']
            ? ret.replace(':userId', this.$route.params['userId'])
            : '';
        }
        if (originalTo.indexOf(':lockerId') !== -1) {
          ret = this.$route.params['lockerId']
            ? ret.replace(':lockerId', this.$route.params['lockerId'])
            : '';
        }
        if (originalTo.indexOf(':spId') !== -1) {
          ret = this.$route.params['spId']
            ? ret.replace(':spId', this.$route.params['spId'])
            : '';
        }

        if (ret.substring(0, 6) == '/mayor') {
          return UrlHelper.mayorUrl(ret);
        }
      }

      else {
        if (originalTo.params && originalTo.params.hasOwnProperty('userId')) {
          if (!this.$route.params || !this.$route.params['userId']) {
            return null;
          }
          originalTo.params.userId = this.$route.params['userId'];
        }
        if (originalTo.params && originalTo.params.hasOwnProperty('lockerId')) {
          if (!this.$route.params || !this.$route.params['lockerId']) {
            return null;
          }
          originalTo.params.lockerId = this.$route.params['lockerId'];
        }
        if (originalTo.params && originalTo.params.hasOwnProperty('spId')) {
          if (!this.$route.params || !this.$route.params['spId']) {
            return null;
          }
          originalTo.params.spId = this.$route.params['spId'];
        }
        if (originalTo.params && originalTo.params.hasOwnProperty('communityId')) {
          if (!this.$route.params || !this.$route.params['communityId']) {
            return null;
          }
          originalTo.params.communityId = this.$route.params['communityId'];
        }

      }

      return ret;
    }
    protected setLanguage(language: string) {
      setLocale(language as ELanguages);
    }

    onCloseDrawer() {
      this.drawer = false;
    }

    private isLang(language: string) {
      return this.$i18n.locale === language;
    }

    get userInitials(): string {
      if (!this.userData || !this.userData.email) return '?';
      return this.userData.email.charAt(0).toUpperCase();
    }

    private userIsAdmin() {
      return PermissionHelper.hasPermissionAny(this.userData, EUserPermissions.PERMISSION_ADMINISTRATOR);
    }

    private userIsDeveloper() {
      if (!this.userData) return false;
      return PermissionHelper.hasPermissionAny(this.userData, EUserPermissions.PERMISSION_DEVELOPER);
    }

    private userIsDeveloperOrAdmin() {
      if (!this.userData) return false;
      return PermissionHelper.hasPermissionAny(this.userData, EUserPermissions.PERMISSION_DEVELOPER)
        || PermissionHelper.hasPermissionAny(this.userData, EUserPermissions.PERMISSION_ADMINISTRATOR);
    }

    private userIsDeveloperOrProduct(){
      if (!this.userData) return false;
      return PermissionHelper.hasPermissionAny(this.userData, EUserPermissions.PERMISSION_DEVELOPER)
          || PermissionHelper.hasPermissionAny(this.userData, EUserPermissions.PERMISSION_PRODUCT);
    }

  }

